import React, { useState, useMemo } from 'react';
import type { DaysOfCoverResponse } from '../../types/daysOfCover';

interface DataTableProps {
  data: DaysOfCoverResponse | null;
}

type SortField = 'sku' | 'name' | 'inventoryC' | 'inventoryD' | 'inventoryE' | 'dsr' | 'daysOfCover' | 'active';
type SortDirection = 'asc' | 'desc';
type FilterType = 'all' | 'critical' | 'low' | 'good' | 'high' | 'excess' | 'active' | 'inactive';

const DataTable: React.FC<DataTableProps> = ({ data }) => {
  const [sortField, setSortField] = useState<SortField>('daysOfCover');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAndSortedData = useMemo(() => {
    if (!data || !data.rows.length) return [];

    let filtered = data.rows.filter(row => {
      const sku = row[0] || '';
      const name = row[1] || '';
      const daysOfCover = parseFloat(row[6]) || 0;
      const active = (row[7] || '').toLowerCase() === 'yes';

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        if (!sku.toLowerCase().includes(searchLower) && 
            !name.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Coverage filter
      switch (filter) {
        case 'critical':
          return daysOfCover < 3 && active;
        case 'low':
          return daysOfCover >= 3 && daysOfCover < 7 && active;
        case 'good':
          return daysOfCover >= 7 && daysOfCover < 14 && active;
        case 'high':
          return daysOfCover >= 14 && daysOfCover <= 30 && active;
        case 'excess':
          return daysOfCover > 30 && active;
        case 'active':
          return active;
        case 'inactive':
          return !active;
        default:
          return true;
      }
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortField) {
        case 'sku':
          aVal = a[0] || '';
          bVal = b[0] || '';
          break;
        case 'name':
          aVal = a[1] || '';
          bVal = b[1] || '';
          break;
        case 'inventoryC':
          aVal = parseFloat(a[2]) || 0;
          bVal = parseFloat(b[2]) || 0;
          break;
        case 'inventoryD':
          aVal = parseFloat(a[3]) || 0;
          bVal = parseFloat(b[3]) || 0;
          break;
        case 'inventoryE':
          aVal = parseFloat(a[4]) || 0;
          bVal = parseFloat(b[4]) || 0;
          break;
        case 'dsr':
          aVal = parseFloat(a[5]) || 0;
          bVal = parseFloat(b[5]) || 0;
          break;
        case 'daysOfCover':
          aVal = parseFloat(a[6]) || 0;
          bVal = parseFloat(b[6]) || 0;
          break;
        case 'active':
          aVal = (a[7] || '').toLowerCase() === 'yes' ? 1 : 0;
          bVal = (b[7] || '').toLowerCase() === 'yes' ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data, sortField, sortDirection, filter, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const exportToCSV = () => {
    if (!data || !filteredAndSortedData.length) return;

    const headers = data.headers.join(',');
    const rows = filteredAndSortedData.map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `days-of-cover-${data.client}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getDaysOfCoverColor = (value: number) => {
    if (value < 3) return 'text-red-600 dark:text-red-400 font-semibold';
    if (value < 7) return 'text-orange-600 dark:text-orange-400 font-medium';
    if (value <= 30) return 'text-green-600 dark:text-green-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  if (!data || !data.rows.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Inventory Data</h3>
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Inventory Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredAndSortedData.length} of {data.rows.length} items
            </p>
          </div>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search SKU or product name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterType)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All Items</option>
              <option value="critical">Critical (&lt;3 days)</option>
              <option value="low">Low (3-7 days)</option>
              <option value="good">Good (7-14 days)</option>
              <option value="high">High (14-30 days)</option>
              <option value="excess">Excess (&gt;30 days)</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table - Fixed width layout for desktop */}
      <div className="overflow-y-auto max-h-[600px] pr-2 scrollbar-thin scrollbar-thumb-gray-300/90 scrollbar-track-transparent dark:scrollbar-thumb-gray-600/90">
        <table className="w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
            <tr>
              {[
                { key: 'sku', label: 'SKU', field: 'sku' as SortField, width: 'w-[120px]', align: 'text-left' },
                { key: 'name', label: 'Product Name', field: 'name' as SortField, width: 'w-[35%] min-w-[240px]', align: 'text-left' },
                { key: 'inventoryC', label: 'MFN\nInventory', field: 'inventoryC' as SortField, width: 'w-[100px]', align: 'text-right' },
                { key: 'inventoryD', label: 'FBA\nInventory', field: 'inventoryD' as SortField, width: 'w-[100px]', align: 'text-right' },
                { key: 'inventoryE', label: 'Total\nInventory', field: 'inventoryE' as SortField, width: 'w-[100px]', align: 'text-right' },
                { key: 'dsr', label: 'DSR', field: 'dsr' as SortField, width: 'w-[90px]', align: 'text-right' },
                { key: 'daysOfCover', label: 'Days of\nCover', field: 'daysOfCover' as SortField, width: 'w-[110px]', align: 'text-right' },
                { key: 'active', label: 'Active', field: 'active' as SortField, width: 'w-[80px]', align: 'text-left' }
              ].map((header) => (
                <th
                  key={header.key}
                  onClick={() => handleSort(header.field)}
                  className={`${header.width} px-3 py-3 ${header.align} text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none`}
                >
                  <div className={`flex items-center ${header.align === 'text-right' ? 'justify-end' : 'justify-start'}`}>
                    <span className="truncate whitespace-pre-line text-center leading-tight">{header.label}</span>
                    {sortField === header.field && (
                      <svg
                        className={`ml-1 h-4 w-4 flex-shrink-0 transform ${sortDirection === 'desc' ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAndSortedData.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                {/* SKU */}
                <td className="w-[120px] px-3 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                  <div className="truncate" title={row[0]}>
                    {row[0]}
                  </div>
                </td>
                {/* Product Name - Allow wrapping */}
                <td className="w-[35%] min-w-[240px] px-3 py-4 text-sm text-gray-900 dark:text-gray-100">
                  <div
                    className="line-clamp-2 leading-tight cursor-help"
                    title={row[1]}
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row[1]}
                  </div>
                </td>
                {/* INV C */}
                <td className="w-[100px] px-3 py-4 text-sm text-gray-900 dark:text-gray-100 text-right">
                  <div className="truncate" title={parseFloat(row[2] || '0').toLocaleString()}>
                    {parseFloat(row[2] || '0').toLocaleString()}
                  </div>
                </td>
                {/* INV D */}
                <td className="w-[100px] px-3 py-4 text-sm text-gray-900 dark:text-gray-100 text-right">
                  <div className="truncate" title={parseFloat(row[3] || '0').toLocaleString()}>
                    {parseFloat(row[3] || '0').toLocaleString()}
                  </div>
                </td>
                {/* INV E */}
                <td className="w-[100px] px-3 py-4 text-sm text-gray-900 dark:text-gray-100 text-right">
                  <div className="truncate" title={parseFloat(row[4] || '0').toLocaleString()}>
                    {parseFloat(row[4] || '0').toLocaleString()}
                  </div>
                </td>
                {/* DSR */}
                <td className="w-[90px] px-3 py-4 text-sm text-gray-900 dark:text-gray-100 text-right">
                  <div className="truncate" title={parseFloat(row[5] || '0').toFixed(1)}>
                    {parseFloat(row[5] || '0').toFixed(1)}
                  </div>
                </td>
                {/* Days of Cover */}
                <td className={`w-[110px] px-3 py-4 text-sm text-right font-medium ${getDaysOfCoverColor(parseFloat(row[6] || '0'))}`}>
                  <div className="truncate" title={parseFloat(row[6] || '0').toFixed(1)}>
                    {parseFloat(row[6] || '0').toFixed(1)}
                  </div>
                </td>
                {/* Active */}
                <td className="w-[80px] px-3 py-4 text-sm text-gray-900 dark:text-gray-100">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full truncate ${
                    (row[7] || '').toLowerCase() === 'yes'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {(row[7] || '').toLowerCase() === 'yes' ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSortedData.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No items match the current filters
        </div>
      )}
    </div>
  );
};

export default DataTable;